/**
 * 声音库「用 TA 出片 →」的**分流菜单**(2026-08-08 IA 收尾,设计稿 §3.4)。
 *
 * ★为什么是菜单而不是按 usage 直跳:选中一个音色只说明用户要这个**声音**,
 *   说明不了他要不要画面 —— 三条线全都用音色。按 usage 单跳会把
 *   「播报音色 + 想出镜」的人扔进纯音频线,他得再横跳一次才回得来。
 *   所以三条线全列,usage 只决定**哪一条打「推荐」**(设计稿原话:
 *   「问一句去哪条线(或按音色 usage 推荐)」—— 这里两个都做了)。
 *
 * ★音色随导航 state 带到落地线:三个 dh preset kind 现在都收 `presetVoice`,
 *   sceneKey 因此打的是**真去的那条线**,不再一律 `dh_talking`。
 */
import { ChevronDown } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import {
  DH_LINE_ROUTE,
  DH_VOICE_USAGE_LINE,
  type DhLineKey,
} from "@/entities/dh-profile/model/dhLines";
import { DH_LINE_META } from "../DigitalHumanCreatePage";

/** kind 与线一一对应(打标用 —— 落哪条线就打哪条线的 sceneKey)。 */
const LINE_PRESET_KIND: Record<DhLineKey, "dh_talking" | "dh_photo" | "dh_tts"> = {
  talking: "dh_talking",
  photo: "dh_photo",
  tts: "dh_tts",
};

const LINES: DhLineKey[] = ["talking", "photo", "tts"];

export interface DhVoiceGoLineMenuProps {
  voiceId: string;
  voiceLabel: string;
  /** 预置音色的用途(broadcast/social/explain/emotion/cartoon);决定哪条线打「推荐」。 */
  usage?: string | null;
  /**
   * 「我的声纹」直达(2026-09-08 声音 V2):带声纹 id 时 state 走声纹口径
   * (scenePreset.voiceProfileId,落地页选中「我的声纹」),不再把 voiceId 当预置音色上送 ——
   * 此时 voiceId 只作 testid/aria 锚(传 `profile-<id>` 之类即可)。
   */
  voiceProfileId?: number;
  onGo: (route: string, state: unknown) => void;
}

export function DhVoiceGoLineMenu({
  voiceId,
  voiceLabel,
  usage,
  voiceProfileId,
  onGo,
}: DhVoiceGoLineMenuProps) {
  const recommended = usage ? DH_VOICE_USAGE_LINE[usage] : undefined;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`用 ${voiceLabel} 出片`}
          data-testid={`dh-voice-go-${voiceId}`}
          className="h-auto self-start px-0 py-0 text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-primary"
        >
          用 TA 出片
          <ChevronDown className="ml-0.5 h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          带「{voiceLabel}」去哪条线?
        </DropdownMenuLabel>
        {LINES.map((l) => (
          <DropdownMenuItem
            key={l}
            data-testid={`dh-voice-go-${voiceId}-${l}`}
            onSelect={() =>
              onGo(DH_LINE_ROUTE[l], {
                scenePreset: {
                  kind: LINE_PRESET_KIND[l],
                  ...(voiceProfileId != null ? { voiceProfileId } : { presetVoice: voiceId }),
                },
              })
            }
            className="flex-col items-start gap-0.5"
          >
            <span className="flex w-full items-center gap-1.5 text-xs font-medium">
              {DH_LINE_META[l].title}
              {recommended === l && (
                <span className="rounded-sm bg-primary/10 px-1 py-px text-2xs leading-none text-primary">
                  推荐
                </span>
              )}
            </span>
            <span className="text-2xs text-muted-foreground">{DH_LINE_META[l].switchHint}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
